import {Button, Grid, Typography, TableRow, TableCell, TableBody, Table} from '@material-ui/core';
import AccountRouteConstants from 'Agent/Accounts/AccountRouteConstants';
import 'Agent/Accounts/components/Card.sass';
import {AccountDetail, Bill} from 'Agent/Accounts/models/AccountDetail';
import AccountImagesCard from 'Agent/Accounts/components/AccountImagesCard';
import {Invoice} from 'Agent/Accounts/models/Invoice';
import {ModalState, showModal} from 'Core/ducks/modal';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import React, {Fragment} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {RouteComponentProps, Link} from 'react-router-dom';
import AllstateCard from 'Shared/components/AllstateCard';
import CardField from 'Shared/components/CardField';
import FileDownloadButton, {FileExtension} from 'Shared/components/FileDownloadButton';
import RequireScope from 'Shared/components/RequireScope';
import withSelectedAgency, {WithSelectedAgencyProps} from 'Shared/components/withSelectedAgency';
import {Scope} from 'Shared/constants/Scope';
import {addFilterParameters} from 'Shared/utilities/addFilterParameters';
import {toFormattedDateAsUTC} from 'Shared/utilities/DateFormatter';
import {toMoneyValue} from 'Shared/utilities/FormatMoney';
import {prop} from 'Shared/utilities/prop';
import useReactRouter from 'use-react-router';
import {UserClaims} from 'Users/models/Claims';
import {selectUserScopes} from 'Users/selectors/userProfileSelectors';
import PolicyRouteConstants from 'Agent/Policies/PolicyRouteConstants';

type AccountDetailsCardProps = {
  accountDetail: AccountDetail;
  userClaims: UserClaims;
} & WithSelectedAgencyProps;

export const AccountDetailCards = ({accountDetail, userClaims, selectedAgency}: AccountDetailsCardProps) => {
  const dispatch = useDispatch();
  const {history, match}: RouteComponentProps<{accountId: string}> = useReactRouter();
  const scopes = useSelector(selectUserScopes);
  const getBillStatementName = (bill: Bill) => `Bill-Statement-${toFormattedDateAsUTC(bill.invoiceDate)}.pdf`;
  const formatPendingPaymentDate = (value: string) => (value === '01/01/0001' ? 'N/A' : value);

  const isInvoiceBefore = (invoice1: Invoice, invoice2: Invoice) =>
    new Date(invoice2.invoiceDate!).getTime() - new Date(invoice1.invoiceDate!).getTime();

  return (
    <div className="account-details">
      <AllstateCard title="Account Details" className="account-details__detail">
        <div className="card__content">
          <Typography className="card__title">{accountDetail.name}</Typography>
          <CardField name="Account Number" value={accountDetail.number} />
          <CardField name="Servicing Agent Number" value={accountDetail.servicingAgentNumber} />
          <CardField name="Account Team" value={accountDetail.accountTeam} />
          <CardField
            name="Source"
            value={pipe(
              O.fromNullable(accountDetail.source),
              O.map(prop('name')),
              O.chain(O.fromNullable),
              O.getOrElse(() => '')
            )}
          />
          <CardField name="Status" value={accountDetail.status} />
          <CardField name="Effective Date" value={toFormattedDateAsUTC(accountDetail.accountEffectiveDate)} />
          <CardField name="Agent Of Record" value={accountDetail.agentOfRecordNumber} />
          <CardField name="Annualized Premium" value={toMoneyValue(accountDetail.annualizedPremium)} />
          <CardField name="Contact Name" value={accountDetail.contact} />
          <CardField name="Contact Number" value={accountDetail.phoneNumber} />
          <CardField name="E-Delivery" value={accountDetail.eDelivery ? 'Yes' : 'No'} />
          <CardField name="Group Number" value={accountDetail.groupNumber} />
          <CardField name="Termination Date" value={toFormattedDateAsUTC(accountDetail.accountTerminationDate)} />
          <CardField name="Termination Reason" value={accountDetail.terminationCodeDesc} />
          <CardField name="Last App Submitted Date" value={toFormattedDateAsUTC(accountDetail.lastAppSubmittedDate)} />
        </div>
      </AllstateCard>
      <div className="account-details__information">
        {accountDetail.address && (
          <AllstateCard title="Address Information">
            <CardField name="Full Address" value={accountDetail.address.fullAddress} />
            <CardField name="State" value={accountDetail.address.state} />
            <CardField name="City" value={accountDetail.address.city} />
            <CardField name="Zip Code" value={accountDetail.address.zipCode} />
          </AllstateCard>
        )}
        <RequireScope scopes={scopes} scope={Scope.ViewAccountImages}>
          <AccountImagesCard selectedAgency={selectedAgency} />
        </RequireScope>
        {accountDetail.commissionAgents && accountDetail.commissionAgents.length > 0 && (
          <AllstateCard title="Commission Agents">
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell>Number</TableCell>
                  <TableCell>Name</TableCell>
                </TableRow>
                {accountDetail.commissionAgents.map((commissionAgent, index) => (
                  <TableRow key={`commission-agent_${index}`}>
                    <TableCell>{commissionAgent.number}</TableCell>
                    <TableCell>{commissionAgent.name}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </AllstateCard>
        )}
        {accountDetail.payments && accountDetail.payments.length > 0 && (
          <RequireScope scopes={scopes} scope={Scope.ViewAccountBilling}>
            <AllstateCard title="Payments">
              <CardField name="Payment Frequency" value={accountDetail.paymentFrequency} />
              {accountDetail.payments.map((payment, index) => (
                <Fragment key={`${payment.date}-${index}`}>
                  <CardField name="Date" value={toFormattedDateAsUTC(payment.date)} />
                  <CardField name="Amount" value={toMoneyValue(payment.amount)} />
                  {payment.hasDiscrepancies && (
                    <Button
                      color="primary"
                      onClick={() => {
                        history.push(
                          AccountRouteConstants.ACCOUNT_DISCREPANCY_SUMMARIES(
                            match.params.accountId,
                            accountDetail.number!,
                            accountDetail.source!.id.toString(),
                            payment.date.toString()
                          )
                        );
                      }}
                    >
                      View Discrepancies
                    </Button>
                  )}
                </Fragment>
              ))}
            </AllstateCard>
          </RequireScope>
        )}
        {accountDetail.pendingPayment && (
          <AllstateCard title="Pending Payments">
            <CardField name="Date Created" value={formatPendingPaymentDate(toFormattedDateAsUTC(accountDetail.pendingPayment.processDate))} />
            <CardField name="Pending Payment Reason" value={accountDetail.pendingPayment.statusDescription} />
          </AllstateCard>
        )}
        {accountDetail.invoices && accountDetail.invoices.length > 0 && (
          <AllstateCard className="account-details__bills" title="Invoices">
            <Grid container direction="row" justify="center" alignItems="center">
              <Grid xs={4} container justify="center" item>
                <Typography className="card__label">Bill Date</Typography>
              </Grid>
              <Grid xs={4} container justify="center" item>
                <Typography className="card__label">Is Delinquent</Typography>
              </Grid>
              <Grid xs={4} container justify="center" item />
              {accountDetail.invoices &&
                accountDetail.invoices.sort(isInvoiceBefore).map(bill => (
                  <Fragment key={bill.documentId}>
                    <Grid xs={4} container justify="center" item>
                      <Typography className="card__value">{toFormattedDateAsUTC(bill.invoiceDate)}</Typography>
                    </Grid>
                    <Grid xs={4} container justify="center" item>
                      {bill.isDelinquent ? 'Yes' : 'No'}
                    </Grid>
                    <Grid xs={4} container justify="center" item>
                      <Typography>
                        {Boolean(bill.documentId) && (
                          <FileDownloadButton
                            downloadedFileName={getBillStatementName(bill)}
                            displayTooltip={false}
                            extension={FileExtension.PDF}
                            relativeFileLocation={`/accounts/billing/${
                              bill.documentId
                            }/accountNumber/${accountDetail.number!}/document${addFilterParameters({
                              encryptedIdentifier: selectedAgency.encryptedIdentifier,
                            })}`}
                          >
                            Download
                          </FileDownloadButton>
                        )}
                      </Typography>
                    </Grid>
                  </Fragment>
                ))}
            </Grid>
          </AllstateCard>
        )}
      </div>
      <div className="account-details__actions-section">
        <RequireScope scopes={scopes} scope={Scope.PolicySearch}>
          <Link to={PolicyRouteConstants.POLICIES_BY_ACCOUNT(accountDetail.number!)}>
            <Button fullWidth className="account-details__view-policies-button" variant="contained" color="primary">
              View Policies
            </Button>
          </Link>
        </RequireScope>
        <RequireScope scopes={scopes} scope={Scope.SearchPolicyReports}>
          <Button
            color="primary"
            variant="contained"
            onClick={() =>
              dispatch(
                showModal(
                  ModalState.RequestPolicyInfoReport({
                    userClaims,
                    accountDetail,
                    email: selectedAgency.email,
                  })
                )
              )
            }
          >
            Request PIR
          </Button>
        </RequireScope>
      </div>
    </div>
  );
};

export default withSelectedAgency(AccountDetailCards);
